import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  Link as MuiLink,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { crlApi } from '../../services/api';

const scoreColors = {
  easy: '#4caf50',
  moderate: '#ff9800',
  difficult: '#f44336',
  veryDifficult: '#e91e63',
};

// Runway color thresholds
const getRunwayColor = (months) => {
  if (!months) return '#999';
  if (months < 12) return '#e53935'; // Critical - Red
  if (months < 24) return '#f57c00'; // Limited - Orange
  if (months < 36) return '#fbc02d'; // Moderate - Yellow
  return '#4caf50'; // Strong - Green
};

const getRunwayLabel = (months) => {
  if (!months) return 'Unknown';
  if (months < 12) return 'Critical';
  if (months < 24) return 'Limited';
  if (months < 36) return 'Moderate';
  return 'Strong';
};

const formatCurrency = (value) => {
  if (!value) return '-';
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(2)}`;
};

// Deficiency category configuration
const deficiencyCategories = [
  { key: 'has_clinical_efficacy_issue', label: 'Clinical Efficacy', color: '#e53935' },
  { key: 'has_clinical_safety_issue', label: 'Clinical Safety', color: '#d32f2f' },
  { key: 'has_product_quality_cmc_issue', label: 'Product Quality/CMC', color: '#f57c00' },
  { key: 'has_facility_inspection_issue', label: 'Facility Inspection', color: '#7b1fa2' },
  { key: 'has_nonclinical_issue', label: 'Nonclinical', color: '#1976d2' },
  { key: 'has_clinical_pharmacology_issue', label: 'Clinical Pharmacology', color: '#388e3c' },
  { key: 'has_device_human_factors_issue', label: 'Device/Human Factors', color: '#00796b' },
  { key: 'has_labeling_issue', label: 'Labeling', color: '#5d4037' },
];

function LetterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [letter, setLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFullText, setShowFullText] = useState(false);
  const [textModalOpen, setTextModalOpen] = useState(false);

  useEffect(() => {
    loadLetter();
  }, [id]);

  const loadLetter = async () => {
    try {
      setLoading(true);
      const response = await crlApi.getLetter(id);
      setLetter(response.data);
    } catch (err) {
      setError('Failed to load letter details.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (!score) return '#999';
    if (score < 2) return scoreColors.easy;
    if (score < 3) return scoreColors.moderate;
    if (score < 4) return scoreColors.difficult;
    return scoreColors.veryDifficult;
  };

  const getDifficultyLabel = (score) => {
    if (!score) return 'Unknown';
    if (score < 2) return 'Low - Minor Issues';
    if (score < 3) return 'Moderate';
    if (score < 4) return 'High - Significant Issues';
    return 'Very High - Major Issues';
  };

  const getAgreementLabel = (level) => {
    if (!level) return 'unknown';
    if (level === 'high') return 'high agreement';
    if (level === 'medium') return 'medium agreement';
    return 'low agreement';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Build agent scores array from flat API fields
  const getAgentScores = () => {
    if (!letter) return [];
    const agents = [];

    if (letter.regulatory_expert_score != null) {
      agents.push({
        name: 'Regulatory Expert',
        score: letter.regulatory_expert_score,
        rationale: letter.regulatory_expert_rationale,
      });
    }
    if (letter.industry_analyst_score != null) {
      agents.push({
        name: 'Industry Analyst',
        score: letter.industry_analyst_score,
        rationale: letter.industry_analyst_rationale,
      });
    }
    if (letter.clinical_development_score != null) {
      agents.push({
        name: 'Clinical Development Lead',
        score: letter.clinical_development_score,
        rationale: letter.clinical_development_rationale,
      });
    }
    return agents;
  };

  // Get active deficiency categories
  const getActiveDeficiencies = () => {
    if (!letter) return [];
    return deficiencyCategories.filter(cat => letter[cat.key]);
  };

  // Get inactive deficiency categories
  const getInactiveDeficiencies = () => {
    if (!letter) return [];
    return deficiencyCategories.filter(cat => !letter[cat.key]);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!letter) {
    return <Alert severity="warning">Letter not found.</Alert>;
  }

  const agentScores = getAgentScores();
  const activeDeficiencies = getActiveDeficiencies();
  const inactiveDeficiencies = getInactiveDeficiencies();

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/crl/letters')}
        sx={{ mb: 2 }}
      >
        Back to Letters
      </Button>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {letter.application_number}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Company: {letter.company_name || 'Unknown Company'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Date: {formatDate(letter.letter_date)}
            </Typography>
            {letter.therapeutic_area && (
              <Typography variant="body2" color="text.secondary">
                Area: {letter.therapeutic_area}
              </Typography>
            )}
          </Box>
          {letter.raw_text && (
            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={() => setTextModalOpen(true)}
            >
              View Letter Text
            </Button>
          )}
        </Box>

        {/* Overall Difficulty Score Banner */}
        {letter.mean_difficulty_score != null && (
          <Paper
            sx={{
              p: 3,
              mt: 3,
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              borderLeft: `4px solid ${getScoreColor(letter.mean_difficulty_score)}`,
            }}
          >
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" fontWeight="bold" color="#92400e" gutterBottom>
                  Overall Difficulty Score
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="h3" fontWeight="bold" sx={{ color: getScoreColor(letter.mean_difficulty_score) }}>
                    {letter.mean_difficulty_score.toFixed(2)}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    / 5.0 ({getAgreementLabel(letter.agent_agreement_level)})
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {getDifficultyLabel(letter.mean_difficulty_score)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" fontWeight="bold" color="#92400e" gutterBottom>
                  Estimated Resolution Timeline
                </Typography>
                {letter.estimated_months_optimistic && letter.estimated_months_pessimistic && (
                  <Typography variant="h5" fontWeight="bold">
                    {letter.estimated_months_optimistic} - {letter.estimated_months_pessimistic} months
                  </Typography>
                )}
                {letter.estimated_months_expected && (
                  <Typography variant="body2" color="text.secondary">
                    Expected: {letter.estimated_months_expected} months
                  </Typography>
                )}
              </Grid>
              {letter.ml_predicted_months != null && (
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" fontWeight="bold" color="#92400e" gutterBottom>
                    ML Prediction
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {letter.ml_predicted_months.toFixed(1)} months
                  </Typography>
                  {letter.ml_approval_probability != null && (
                    <Typography variant="body2" color="text.secondary">
                      Approval probability: {(letter.ml_approval_probability * 100).toFixed(0)}%
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>
          </Paper>
        )}
      </Paper>

      {/* Agent Analysis */}
      {agentScores.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            AI Agent Analysis
          </Typography>
          <Grid container spacing={3}>
            {agentScores.map((agent, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {agent.name}
                      </Typography>
                      <Chip
                        label={agent.score?.toFixed(1) || '-'}
                        sx={{
                          bgcolor: getScoreColor(agent.score),
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          height: 32,
                        }}
                      />
                    </Box>
                    {agent.rationale && (
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {agent.rationale}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Deficiency Summary */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Primary Deficiency Summary
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              {letter.primary_deficiency_summary || 'No summary available.'}
            </Typography>
          </Paper>

          {/* Deficiency Categories */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Deficiency Categories ({letter.deficiency_category_count || activeDeficiencies.length})
            </Typography>

            {/* Active deficiencies */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
              {activeDeficiencies.map((cat, idx) => (
                <Chip
                  key={idx}
                  icon={<WarningIcon />}
                  label={cat.label}
                  sx={{
                    bgcolor: cat.color,
                    color: 'white',
                    fontWeight: 'medium',
                    '& .MuiChip-icon': { color: 'white' },
                  }}
                />
              ))}
            </Box>

            {/* Inactive deficiencies */}
            {inactiveDeficiencies.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {inactiveDeficiencies.map((cat, idx) => (
                  <Chip
                    key={idx}
                    label={cat.label}
                    variant="outlined"
                    size="small"
                    sx={{ color: '#999', borderColor: '#ddd' }}
                    icon={<CancelIcon sx={{ color: '#ccc !important' }} />}
                  />
                ))}
              </Box>
            )}
          </Paper>

          {/* Critical Flags */}
          {letter.new_clinical_trial_required && (
            <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Critical Flags
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label="New Clinical Trial Required"
                  color="error"
                  size="small"
                  sx={{ mr: 1 }}
                />
                {letter.has_clinical_efficacy_issue && (
                  <Chip label="New Efficacy Trial" color="error" variant="outlined" size="small" sx={{ mr: 1 }} />
                )}
              </Box>
            </Alert>
          )}

          {/* Full Letter Text */}
          {letter.raw_text && (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold">
                  Full Letter Text
                </Typography>
                <Button
                  variant="text"
                  onClick={() => setShowFullText(!showFullText)}
                  endIcon={showFullText ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  {showFullText ? 'Hide' : 'Show'} Full Text
                </Button>
              </Box>
              <Collapse in={showFullText}>
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                    maxHeight: 500,
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6,
                  }}
                >
                  {letter.raw_text}
                </Box>
              </Collapse>
            </Paper>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Product Information */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Product Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Application Type</Typography>
                <Typography fontWeight="medium">
                  {letter.application_number?.startsWith('NDA') ? 'NDA' :
                   letter.application_number?.startsWith('BLA') ? 'BLA' :
                   letter.application_number?.startsWith('ANDA') ? 'ANDA' : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Application Number</Typography>
                <Typography fontWeight="medium">{letter.application_number || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Company</Typography>
                <Typography fontWeight="medium">{letter.company_name || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Therapeutic Area</Typography>
                <Typography fontWeight="medium">{letter.therapeutic_area || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Letter Date</Typography>
                <Typography fontWeight="medium">{formatDate(letter.letter_date)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Letter Type</Typography>
                <Typography fontWeight="medium">{letter.letter_type || 'Complete Response Letter'}</Typography>
              </Box>
              {letter.approver_name && (
                <Box>
                  <Typography variant="caption" color="text.secondary">FDA Approver</Typography>
                  <Typography fontWeight="medium">{letter.approver_name}</Typography>
                  {letter.approver_title && (
                    <Typography variant="body2" color="text.secondary">{letter.approver_title}</Typography>
                  )}
                </Box>
              )}
            </Box>
          </Paper>

          {/* Financial Health */}
          {letter.stock_data && Object.keys(letter.stock_data).length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Financial Health
              </Typography>
              {letter.stock_data.financials_as_of && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  As of {letter.stock_data.financials_as_of}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Ticker</Typography>
                  <Typography fontWeight="medium">{letter.stock_data.ticker || '-'}</Typography>
                </Box>
                {letter.stock_data.runway_months != null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Cash Runway</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight="medium" sx={{ color: getRunwayColor(letter.stock_data.runway_months) }}>
                        {letter.stock_data.runway_months.toFixed(1)} months
                      </Typography>
                      <Chip
                        label={getRunwayLabel(letter.stock_data.runway_months)}
                        size="small"
                        sx={{
                          bgcolor: getRunwayColor(letter.stock_data.runway_months),
                          color: 'white',
                          fontWeight: 'medium',
                        }}
                      />
                    </Box>
                  </Box>
                )}
                {letter.stock_data.monthly_burn_rate != null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Monthly Burn Rate</Typography>
                    <Typography fontWeight="medium">{formatCurrency(letter.stock_data.monthly_burn_rate)}</Typography>
                  </Box>
                )}
                {letter.stock_data.cash_position != null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Cash Position</Typography>
                    <Typography fontWeight="medium">{formatCurrency(letter.stock_data.cash_position)}</Typography>
                  </Box>
                )}
                {letter.stock_data.cash_per_share != null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Cash Per Share</Typography>
                    <Typography fontWeight="medium">${letter.stock_data.cash_per_share.toFixed(2)}</Typography>
                  </Box>
                )}
                {letter.stock_data.market_cap != null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Market Cap</Typography>
                    <Typography fontWeight="medium">{formatCurrency(letter.stock_data.market_cap)}</Typography>
                  </Box>
                )}
                {letter.stock_data.week_52_high != null && letter.stock_data.week_52_low != null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">52-Week Range</Typography>
                    <Typography fontWeight="medium">
                      ${letter.stock_data.week_52_low.toFixed(2)} - ${letter.stock_data.week_52_high.toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Letter Text Modal */}
      <Dialog
        open={textModalOpen}
        onClose={() => setTextModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Complete Response Letter</Typography>
            <Typography variant="body2" color="text.secondary">
              {letter?.application_number} - {formatDate(letter?.letter_date)}
            </Typography>
          </Box>
          <IconButton onClick={() => setTextModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.7,
              p: 2,
              bgcolor: '#fafafa',
              borderRadius: 1,
            }}
          >
            {letter?.raw_text || 'No letter text available.'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTextModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LetterDetail;
